import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  CountByString,
  CountByWeek,
  DailyConsultationsReasons,
  DailyPatients,
  DailyStatus,
  RankCount,
  WeeklyConsultations,
  WeeklyConsultationsByType,
  WeeklySpecialtyConsultations,
} from '@/core/models/admin.model'
import { MeetingLinkStatistics } from '@/core/models/meeting-link.model'

import { RootState } from '@/redux/rootReducer'

export const selectConsultationStatistics = (state: RootState) => state.statistic.consultation
export const selectMeetingLinkStatistics = (state: RootState) => state.statistic.meetingLink
export const selectPatientStatistics = (state: RootState) => state.statistic.patient
export const selectNetworkSpeed = (state: RootState) => state.statistic.networkSpeed

type StatisticReducerState = {
  consultation: {
    byWeek: CountByWeek[]
    type: {
      type: CountByString[]
      status: CountByString[]
    }
    rank: RankCount[]
    dailyStatus: DailyStatus
    weeklyConsultations: WeeklyConsultations
    dailyReasons: DailyConsultationsReasons
  }
  meetingLink: MeetingLinkStatistics
  networkSpeed: {
    download: number | null
    upload: number | null
  }
  patient: {
    total: number
    byWeek: CountByWeek[]
    dailyPatients: DailyPatients
    weeklyConsultationsByType: WeeklyConsultationsByType
    weeklySpecialty: WeeklySpecialtyConsultations
  }
}

const initialState: StatisticReducerState = {
  consultation: {
    byWeek: [],
    type: {
      type: [],
      status: [],
    },
    rank: [],
    dailyStatus: {
      date: '',
      data: [],
    },
    weeklyConsultations: {
      startDate: '',
      endDate: '',
      data: [],
    },
    dailyReasons: {
      date: '',
      data: [],
    },
  },
  meetingLink: {
    pendingBulkOrders: 0,
    pendingOrders: 0,
    totalMeetingLinks: 0,
    totalOrders: 0,
  },
  networkSpeed: {
    download: null,
    upload: null,
  },
  patient: {
    total: 0,
    byWeek: [],
    dailyPatients: {
      date: '',
      data: [],
    },
    weeklyConsultationsByType: {
      startDate: '',
      endDate: '',
      data: [],
    },
    weeklySpecialty: {
      startDate: '',
      endDate: '',
      data: [],
    },
  },
}

const statisticSlice = createSlice({
  name: 'statistic',
  initialState,
  reducers: {
    getTotalPatient() {},
    setTotalPatient(state, action: PayloadAction<number>) {
      state.patient.total = action.payload
    },
    getDownloadSpeed() {},
    setDownloadSpeed(state, action: PayloadAction<number | null>) {
      state.networkSpeed.download = action.payload
    },
    getUploadSpeed() {},
    setUploadSpeed(state, action: PayloadAction<number | null>) {
      state.networkSpeed.upload = action.payload
    },
    getConsultationsByWeekByEtablissement(state, action: PayloadAction<{ establishmentId: string }>) {},
    setConsultationByWeekByEtablissement(state, action: PayloadAction<CountByWeek[]>) {
      state.consultation.byWeek = action.payload
    },
    getPatientsByWeekByEtablissement(state, action: PayloadAction<{ establishmentId: string }>) {},
    setPatientsByWeekByEtablissement(state, action: PayloadAction<CountByWeek[]>) {
      state.patient.byWeek = action.payload
    },
    getConsultationsStatusCountByEtablissement(state, action: PayloadAction<{ establishmentId: string }>) {},
    setConsultationsStatusCountByEtablissement(state, action: PayloadAction<CountByString[]>) {
      state.consultation.type.status = action.payload
    },
    getConsultationsTypeCountByEtablissement(state, action: PayloadAction<{ establishmentId: string }>) {},
    setConsultationsTypeCountByEtablissement(state, action: PayloadAction<CountByString[]>) {
      state.consultation.type.type = action.payload
    },
    getRankConsultationsCountByEtablissement(state, action: PayloadAction<{ establishmentId: string }>) {},
    setRankConsultationsCountByEtablissement(state, action: PayloadAction<RankCount[]>) {
      state.consultation.rank = action.payload
    },
    getMeetingLinkStatistics() {},
    setMeetingLinkStatistics(state, action: PayloadAction<MeetingLinkStatistics>) {
      state.meetingLink = action.payload
    },

    // New getters for daily and weekly statistics
    getDailyStatus(state, action: PayloadAction<{ establishmentId: string; date: string }>) {},
    getWeeklyConsultations(state, action: PayloadAction<{ establishmentId: string; startDate: string; endDate: string }>) {},
    getDailyPatients(state, action: PayloadAction<{ establishmentId: string; date: string }>) {},
    getWeeklyConsultationsByType(state, action: PayloadAction<{ establishmentId: string; startDate: string; endDate: string }>) {},
    getDailyConsultationsReasons(state, action: PayloadAction<{ establishmentId: string; date: string }>) {},
    getWeeklySpecialtyConsultations(state, action: PayloadAction<{ establishmentId: string; startDate: string; endDate: string }>) {},

    // New actions for setting daily status statistics
    setDailyStatus(state, action: PayloadAction<DailyStatus['data']>) {
      state.consultation.dailyStatus.data = action.payload
    },

    // New actions for setting weekly consultations statistics
    setWeeklyConsultations(state, action: PayloadAction<WeeklyConsultations['data']>) {
      state.consultation.weeklyConsultations.data = action.payload
    },

    // New actions for setting daily new patients statistics
    setDailyPatients(state, action: PayloadAction<DailyPatients['data']>) {
      state.patient.dailyPatients.data = action.payload
    },

    // New actions for setting weekly consultations by type statistics
    setWeeklyConsultationsByType(state, action: PayloadAction<WeeklyConsultationsByType['data']>) {
      state.patient.weeklyConsultationsByType.data = action.payload
    },

    // New actions for setting daily consultations by main reason statistics
    setDailyConsultationsReasons(state, action: PayloadAction<DailyConsultationsReasons['data']>) {
      state.consultation.dailyReasons.data = action.payload
    },

    // New actions for setting weekly specialty consultations statistics
    setWeeklySpecialtyConsultations(state, action: PayloadAction<WeeklySpecialtyConsultations['data']>) {
      state.patient.weeklySpecialty.data = action.payload
    },
  },
})

export const {
  getTotalPatient,
  setTotalPatient,
  getDownloadSpeed,
  getUploadSpeed,
  setDownloadSpeed,
  setUploadSpeed,
  getConsultationsByWeekByEtablissement,
  setConsultationByWeekByEtablissement,
  getPatientsByWeekByEtablissement,
  setPatientsByWeekByEtablissement,
  getConsultationsStatusCountByEtablissement,
  setConsultationsStatusCountByEtablissement,
  getConsultationsTypeCountByEtablissement,
  setConsultationsTypeCountByEtablissement,
  getRankConsultationsCountByEtablissement,
  setRankConsultationsCountByEtablissement,
  getMeetingLinkStatistics,
  setMeetingLinkStatistics,
  getDailyStatus,
  getWeeklyConsultations,
  getDailyPatients,
  getWeeklyConsultationsByType,
  getDailyConsultationsReasons,
  getWeeklySpecialtyConsultations,
  setDailyStatus,
  setWeeklyConsultations,
  setDailyPatients,
  setWeeklyConsultationsByType,
  setDailyConsultationsReasons,
  setWeeklySpecialtyConsultations,
} = statisticSlice.actions

export default statisticSlice.reducer
